/* =========================================================================
   Boreal Luxe — collection page behaviour
   Card gallery + swatches + size add-to-cart, grid density, filters.
   No dependencies. Safe to load with defer.
   ========================================================================= */
(function () {
  'use strict';

  var MONEY_FORMAT =
    (window.BorealCollection && window.BorealCollection.moneyFormat) || '${{amount}}';
  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_ADD_URL = ROOT_URL.replace(/\/$/, '') + '/cart/add.js';

  /* ---------------------------------------------------------------- utils */

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withDecimals = whole + '.' + parts[1];
    var noDecimals = whole;

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return withDecimals;
        case 'amount_no_decimals':
          return noDecimals;
        case 'amount_with_comma_separator':
          return withDecimals.replace(/,/g, ' ').replace('.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return noDecimals.replace(/,/g, ' ');
        case 'amount_with_space_separator':
          return withDecimals.replace(/,/g, ' ');
        case 'amount_no_decimals_with_space_separator':
          return noDecimals.replace(/,/g, ' ');
        case 'amount_with_apostrophe_separator':
          return withDecimals.replace(/,/g, "'");
        default:
          return withDecimals;
      }
    });
  }

  var SIZES_ATTR = '(min-width: 1200px) 460px, (min-width: 750px) 34vw, 50vw';

  function srcsetFor(url) {
    if (!url || url.indexOf('width=900') === -1) return '';
    return [300, 450, 600, 900, 1200]
      .map(function (w) {
        return url.replace('width=900', 'width=' + w) + ' ' + w + 'w';
      })
      .join(', ');
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        node.setAttribute(k, attrs[k]);
      });
    }
    return node;
  }

  /* ----------------------------------------------------------------- card */

  /* Swatches now share a line with the price, so the cap is width-driven:
     three fit comfortably on a phone card, five on a desktop one. */
  var MAX_SWATCHES = window.matchMedia('(min-width: 750px)').matches ? 5 : 3;

  function Card(root) {
    this.root = root;
    var script = root.querySelector('[data-boreal-card-config]');
    if (!script) return;
    try {
      this.data = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!this.data.colours || !this.data.colours.length) return;

    this.track = root.querySelector('[data-bcard-track]');
    this.link = root.querySelector('[data-bcard-link]');
    this.titleLink = root.querySelector('[data-bcard-title-link]');
    this.priceEl = root.querySelector('[data-bcard-price]');
    this.dotsEl = root.querySelector('[data-bcard-dots]');
    this.prevBtn = root.querySelector('[data-bcard-prev]');
    this.nextBtn = root.querySelector('[data-bcard-next]');
    this.swatchEl = root.querySelector('[data-bcard-swatches]');
    this.sizesEl = root.querySelector('[data-bcard-sizes]');
    this.sizeListEl = root.querySelector('[data-bcard-size-list]');
    this.sizeLabelEl = root.querySelector('[data-bcard-sizes-label]');

    this.colourIndex = 0;
    this.slideIndex = 0;

    this.renderSwatches();
    this.renderSlides(this.data.colours[0].slides, true);
    this.renderSizes();
    this.bind();
    /* Exposed so the quick-add handler can reach the instance for a direct
       add-to-cart without re-parsing the config JSON. */
    root.__bcard = this;
    root.setAttribute('data-boreal-card-ready', 'true');
  }

  Card.prototype.colour = function () {
    return this.data.colours[this.colourIndex];
  };

  Card.prototype.renderSwatches = function () {
    if (!this.swatchEl) return;
    var self = this;
    var colours = this.data.colours;
    this.swatchEl.textContent = '';
    /* A single-colour product still shows its swatch — it reads as a deliberate
       colourway rather than a missing feature. */
    if (!colours.length || (colours.length === 1 && !colours[0].name)) return;

    var limit = this.swatchEl.dataset.expanded === 'true' ? colours.length : MAX_SWATCHES;

    colours.slice(0, limit).forEach(function (colour, i) {
      var btn = el('button', 'bcard__swatch' + (i === self.colourIndex ? ' is-active' : ''), {
        type: 'button',
        'aria-label': colour.name,
        title: colour.name
      });
      if (colour.swatchType === 'color') {
        btn.style.backgroundColor = colour.swatch;
      } else if (colour.swatch) {
        btn.style.backgroundImage = 'url(' + colour.swatch + ')';
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.setColour(i);
      });
      btn.addEventListener('mouseenter', function () {
        self.setColour(i);
      });
      self.swatchEl.appendChild(btn);
    });

    if (colours.length > limit) {
      var more = el('button', 'bcard__swatch-more', { type: 'button' });
      more.textContent = '+' + (colours.length - limit);
      more.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.swatchEl.dataset.expanded = 'true';
        self.renderSwatches();
      });
      this.swatchEl.appendChild(more);
    }
  };

  Card.prototype.renderSlides = function (slides, keepExisting) {
    if (!this.track) return;
    var alt = this.data.title;

    if (!keepExisting) {
      this.track.textContent = '';
      slides.forEach(function (src, i) {
        var slide = el('div', 'bcard__slide' + (i === 0 ? ' is-active' : ''));
        slide.setAttribute('data-bcard-slide', '');
        var img = el('img');
        img.src = src;
        img.srcset = srcsetFor(src);
        img.sizes = SIZES_ATTR;
        img.alt = alt;
        img.loading = 'lazy';
        img.width = 900;
        img.height = 1200;
        slide.appendChild(img);
        this.track.appendChild(slide);
      }, this);
    }

    this.slides = this.track.querySelectorAll('[data-bcard-slide]');
    this.slideIndex = 0;
    this.renderDots();
    this.updateSlide();
  };

  Card.prototype.renderDots = function () {
    var count = this.slides ? this.slides.length : 0;
    var multiple = count > 1;

    if (this.prevBtn) this.prevBtn.hidden = !multiple;
    if (this.nextBtn) this.nextBtn.hidden = !multiple;
    if (!this.dotsEl) return;

    this.dotsEl.textContent = '';
    if (!multiple) return;
    for (var i = 0; i < count; i++) {
      this.dotsEl.appendChild(el('span', 'bcard__dot' + (i === 0 ? ' is-active' : '')));
    }
  };

  Card.prototype.updateSlide = function () {
    if (!this.slides) return;
    for (var i = 0; i < this.slides.length; i++) {
      this.slides[i].classList.toggle('is-active', i === this.slideIndex);
    }
    if (this.dotsEl) {
      var dots = this.dotsEl.children;
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-active', d === this.slideIndex);
      }
    }
    if (this.prevBtn) this.prevBtn.disabled = this.slideIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.slideIndex >= this.slides.length - 1;
  };

  Card.prototype.go = function (delta) {
    if (!this.slides || this.slides.length < 2) return;
    var next = this.slideIndex + delta;
    if (next < 0 || next > this.slides.length - 1) return;
    this.slideIndex = next;
    this.updateSlide();
  };

  Card.prototype.setColour = function (index) {
    if (index === this.colourIndex || !this.data.colours[index]) return;
    this.colourIndex = index;
    var colour = this.colour();

    this.renderSlides(colour.slides, false);
    if (this.link) this.link.href = colour.url;
    if (this.titleLink) this.titleLink.href = colour.url;

    var self = this;
    if (this.swatchEl) {
      var btns = this.swatchEl.querySelectorAll('.bcard__swatch');
      for (var i = 0; i < btns.length; i++) {
        btns[i].classList.toggle('is-active', i === self.colourIndex);
      }
    }

    this.renderPrice();
    this.renderSizes();
  };

  Card.prototype.variantsForColour = function () {
    var name = this.colour().name;
    return this.data.variants.filter(function (v) {
      return !name || v.c === name;
    });
  };

  Card.prototype.renderPrice = function () {
    if (!this.priceEl) return;
    var variants = this.variantsForColour();
    if (!variants.length) return;

    var cheapest = variants.reduce(function (a, b) {
      return b.p < a.p ? b : a;
    });

    this.priceEl.textContent = '';
    if (cheapest.cap && cheapest.cap > cheapest.p) {
      var was = el('s', 'bcard__price-was');
      was.textContent = formatMoney(cheapest.cap);
      this.priceEl.appendChild(was);
      var now = el('span', 'bcard__price-now bcard__price-now--sale');
      now.textContent = formatMoney(cheapest.p);
      this.priceEl.appendChild(now);
    } else {
      var only = el('span', 'bcard__price-now');
      only.textContent = formatMoney(cheapest.p);
      this.priceEl.appendChild(only);
    }
  };

  Card.prototype.renderSizes = function () {
    if (!this.sizeListEl) return;
    var self = this;
    var variants = this.variantsForColour().filter(function (v) {
      return v.s;
    });

    this.sizeListEl.textContent = '';
    if (!variants.length) {
      if (this.sizesEl) this.sizesEl.hidden = true;
      return;
    }
    if (this.sizesEl) this.sizesEl.hidden = false;

    variants.forEach(function (v) {
      var btn = el('button', 'bcard__size', { type: 'button', 'data-variant-id': v.id });
      btn.textContent = v.s;
      if (!v.a) {
        btn.disabled = true;
        btn.setAttribute('aria-label', v.s + ' — sold out');
      } else {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          self.addToCart(v.id, btn);
        });
      }
      self.sizeListEl.appendChild(btn);
    });
  };

  Card.prototype.setSizeLabel = function (text, state) {
    if (!this.sizeLabelEl) return;
    this.sizeLabelEl.textContent = text;
    if (this.sizesEl) {
      this.sizesEl.classList.toggle('is-busy', state === 'busy');
      this.sizesEl.classList.toggle('is-done', state === 'done');
    }
  };

  /* UpCart (and most cart apps) intercept /cart/add.js and open their own
     drawer. If we ALSO drive Dawn's cart-drawer we end up with two drawers, and
     closing the app one leaves Dawn's behind with overflow-hidden still on the
     body — the page looks frozen. So: when a cart app is present, post the line
     item and let the app own the UI. */
  function cartAppPresent() {
    return !!(
      window.upcart ||
      window.UpCart ||
      document.querySelector('[id^="upcart"], [class^="upcart"], #upcart-container') ||
      document.querySelector('[id*="rebuy-cart"], .slidecart, #slidecart')
    );
  }

  /* Belt and braces: if a foreign drawer is on screen, make sure Dawn's is not
     also holding the scroll lock. */
  function releaseStrandedDrawer() {
    var dawn = document.querySelector('cart-drawer.active, cart-drawer.animate.active');
    if (!dawn) return;
    var foreign =
      document.body.classList.contains('upcartPopupShow') ||
      document.querySelector('.upcart-open, [class*="upcart"][class*="show"]');
    if (!foreign) return;
    dawn.classList.remove('active');
    dawn.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }

  Card.prototype.addToCart = function (variantId, btn) {
    var self = this;
    if (this.adding) return;
    this.adding = true;
    btn.classList.add('is-busy');
    this.setSizeLabel('Adding…', 'busy');

    var useDawnDrawer = !cartAppPresent();
    var drawer = useDawnDrawer
      ? document.querySelector('cart-drawer') || document.querySelector('cart-notification')
      : null;
    var body = { items: [{ id: variantId, quantity: 1 }] };

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        self.adding = false;
        btn.classList.remove('is-busy');

        if (response.status) {
          self.setSizeLabel(response.description || 'Unavailable', 'done');
          window.setTimeout(function () {
            self.setSizeLabel('Select a size');
          }, 2600);
          return;
        }

        self.setSizeLabel('Added to bag', 'done');
        window.setTimeout(function () {
          self.setSizeLabel('Select a size');
        }, 2600);

        if (drawer && typeof drawer.renderContents === 'function') {
          if (drawer.classList) drawer.classList.remove('is-empty');
          drawer.renderContents(response);
        } else {
          /* Let the cart app pick this up. */
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          document.dispatchEvent(
            new CustomEvent('upcart:cartUpdated', { bubbles: true, detail: response })
          );
        }

        window.setTimeout(releaseStrandedDrawer, 350);
        window.setTimeout(releaseStrandedDrawer, 1200);

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'boreal-collection',
            productVariantId: variantId,
            cartData: response
          });
        }
      })
      .catch(function () {
        self.adding = false;
        btn.classList.remove('is-busy');
        self.setSizeLabel('Something went wrong', 'done');
        window.setTimeout(function () {
          self.setSizeLabel('Select a size');
        }, 2600);
      });
  };

  Card.prototype.bind = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.go(-1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.go(1);
      });
    }
    if (this.dotsEl) {
      this.dotsEl.addEventListener('click', function (e) {
        var dots = Array.prototype.slice.call(self.dotsEl.children);
        var i = dots.indexOf(e.target);
        if (i > -1) {
          e.preventDefault();
          e.stopPropagation();
          self.slideIndex = i;
          self.updateSlide();
        }
      });
      this.dotsEl.style.pointerEvents = 'auto';
    }

    /* Touch swipe. Only hijacks the tap when the finger moves horizontally,
       so vertical page scroll and plain taps on the link still work. */
    var startX = 0;
    var startY = 0;
    var swiping = false;
    var moved = false;

    this.root.addEventListener(
      'touchstart',
      function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        swiping = true;
        moved = false;
      },
      { passive: true }
    );

    this.root.addEventListener(
      'touchmove',
      function (e) {
        if (!swiping) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) moved = true;
      },
      { passive: true }
    );

    this.root.addEventListener('touchend', function (e) {
      if (!swiping) return;
      swiping = false;
      if (!moved) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      self.go(dx < 0 ? 1 : -1);
    });

    /* A finished swipe must not also follow the product link. */
    this.root.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );
  };

  /* ------------------------------------------------------------- wishlist */

  /* Kept in step with every pushState so the popstate handler can tell a real
     filter change from a hash jump. */
  var lastSearch = window.location.search;

  var WISH_KEY = 'boreal:wishlist';

  function readWishlist() {
    try {
      return JSON.parse(window.localStorage.getItem(WISH_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode — ignore */
    }
  }

  /* Records look like { handle, title, url, image, price }. Older builds stored
     bare handle strings, so normalise on read. */
  function normalise(list) {
    return (list || []).map(function (item) {
      return typeof item === 'string' ? { handle: item } : item;
    });
  }

  function indexOfHandle(list, handle) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle === handle) return i;
    }
    return -1;
  }

  /* A heart that only changes colour is invisible feedback, and nothing on the
     site otherwise reveals that a saved page exists. The toast does both jobs. */
  var toastEl = null;
  var toastTimer = null;

  function showToast(message, linkText, linkHref) {
    if (!toastEl) {
      toastEl = el('div', 'bc-toast');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = '';
    var text = el('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (linkText && linkHref) {
      var a = el('a', 'bc-toast__link');
      a.href = linkHref;
      a.textContent = linkText;
      toastEl.appendChild(a);
    }
    window.clearTimeout(toastTimer);
    /* Force a frame so the transition runs on a freshly-created node. */
    window.requestAnimationFrame(function () {
      toastEl.classList.add('is-visible');
    });
    toastTimer = window.setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3200);
  }

  function wishlistUrl() {
    return (window.BorealCollection && window.BorealCollection.wishlistUrl) || '/pages/saved';
  }

  function broadcastWishlist(list, handle) {
    document.dispatchEvent(
      new CustomEvent('boreal:wishlist', {
        detail: { items: list, count: list.length, handle: handle }
      })
    );
    document.querySelectorAll('[data-boreal-wish-count]').forEach(function (el) {
      el.textContent = list.length ? String(list.length) : '';
      el.toggleAttribute('hidden', list.length === 0);
    });
  }

  function initWishlist(scope) {
    var saved = normalise(readWishlist());
    scope.querySelectorAll('[data-bcard-wish]').forEach(function (btn) {
      if (btn.dataset.wishBound) return;
      btn.dataset.wishBound = 'true';
      var handle = btn.getAttribute('data-product-handle');
      if (indexOfHandle(saved, handle) > -1) {
        btn.classList.add('is-saved');
        btn.setAttribute('aria-pressed', 'true');
      }
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var list = normalise(readWishlist());
        var i = indexOfHandle(list, handle);
        if (i > -1) {
          list.splice(i, 1);
          btn.classList.remove('is-saved');
          btn.setAttribute('aria-pressed', 'false');
        } else {
          list.push({
            handle: handle,
            title: btn.getAttribute('data-product-title') || '',
            url: btn.getAttribute('data-product-url') || '',
            image: btn.getAttribute('data-product-image') || '',
            price: btn.getAttribute('data-product-price') || ''
          });
          btn.classList.add('is-saved');
          btn.setAttribute('aria-pressed', 'true');
        }
        writeWishlist(list);
        broadcastWishlist(list, handle);
        if (i > -1) {
          showToast('Removed from your saved list', 'View saved', wishlistUrl());
        } else {
          showToast('Saved', 'View saved', wishlistUrl());
        }
      });
    });
    broadcastWishlist(saved, null);
  }

  /* --------------------------------------------------------- saved page */

  /* ------------------------------------------------------------ saved page

     The saved page used to hand-build a cut-down card from the five fields kept
     in localStorage — image, title, price, url, handle. That was enough to look
     like a grid but nothing else worked: no gallery, no colour swatches, no add
     to cart, because none of the variant data was there to work with.

     Instead it now fetches the real card per handle through the Section
     Rendering API and drops it straight in. The markup is identical to the
     collection grid's, so init() binds exactly the same behaviours and there is
     only ever one card implementation to maintain.
     ---------------------------------------------------------------------- */

  var CARD_SECTION = 'boreal-card-fragment';

  function fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION, {
      headers: { Accept: 'text/html' }
    })
      .then(function (r) {
        /* 404 means the product was deleted or unpublished since it was saved. */
        if (!r.ok) return null;
        return r.text();
      })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-boreal-card]');
      })
      .catch(function () {
        return null;
      });
  }

  /* Wishlists are small, but a shopper with 30 saved items should not open 30
     sockets at once. */
  function pool(items, limit, worker) {
    var results = new Array(items.length);
    var next = 0;
    function run() {
      if (next >= items.length) return Promise.resolve();
      var i = next++;
      return worker(items[i], i).then(function (v) {
        results[i] = v;
        return run();
      });
    }
    var runners = [];
    for (var i = 0; i < Math.min(limit, items.length); i++) runners.push(run());
    return Promise.all(runners).then(function () {
      return results;
    });
  }

  function initWishlistPage(scope) {
    var host = scope.querySelector
      ? scope.querySelector('[data-boreal-wishlist-page]')
      : null;
    if (!host || host.dataset.wishPageBound) return;
    host.dataset.wishPageBound = 'true';

    var quickStyle = host.getAttribute('data-quick-add-style') || '';

    function showEmpty() {
      var link = host.getAttribute('data-empty-link');
      host.innerHTML =
        '<div class="bc-wish__empty"><p>' +
        escapeHtml(host.getAttribute('data-empty-text') || '') +
        '</p>' +
        (link
          ? '<a class="bc-wish__cta" href="' +
            escapeHtml(link) +
            '">' +
            escapeHtml(host.getAttribute('data-empty-link-text') || '') +
            '</a>'
          : '') +
        '</div>';
    }

    function render() {
      var items = normalise(readWishlist()).filter(function (i) {
        return i && i.handle;
      });

      if (!items.length) {
        showEmpty();
        return;
      }

      host.innerHTML =
        '<ul class="bc-grid" data-bc-grid data-density="2" role="list">' +
        items
          .map(function () {
            return '<li class="bc-wish__slot"><span class="bc-wish__skeleton"></span></li>';
          })
          .join('') +
        '</ul>';

      var grid = host.querySelector('[data-bc-grid]');
      var slots = grid.querySelectorAll('.bc-wish__slot');
      var missing = [];

      pool(items, 6, function (item, i) {
        return fetchCard(item.handle).then(function (card) {
          var slot = slots[i];
          if (!slot) return null;
          if (!card) {
            missing.push(item.handle);
            slot.remove();
            return null;
          }
          if (quickStyle) {
            var q = card.querySelector('[data-bcard-quick]');
            if (q) {
              q.className = q.className.replace(
                /bcard__quick--[a-z]+/,
                'bcard__quick--' + quickStyle
              );
            }
          }
          slot.classList.remove('bc-wish__slot');
          slot.innerHTML = '';
          slot.appendChild(card);
          return card;
        });
      }).then(function () {
        /* Products that no longer exist are dropped from storage rather than
           being re-fetched and re-failed on every visit. */
        if (missing.length) {
          var list = normalise(readWishlist()).filter(function (it) {
            return missing.indexOf(it.handle) === -1;
          });
          writeWishlist(list);
          broadcastWishlist(list, null);
        }
        if (!grid.querySelector('[data-boreal-card]')) {
          showEmpty();
          return;
        }
        /* Binds gallery, swatches, quick add, wishlist hearts — the same call
           the collection grid makes. */
        init(host);
      });
    }

    /* Un-hearting a card on this page should remove it from the page, which is
       the one behaviour the saved page needs beyond the collection grid. */
    document.addEventListener('boreal:wishlist', function (e) {
      if (!e.detail || !e.detail.handle) return;
      var saved = normalise(e.detail.items || []);
      if (indexOfHandle(saved, e.detail.handle) > -1) return;
      var card = host.querySelector(
        '[data-bcard-wish][data-product-handle="' + e.detail.handle.replace(/"/g, '') + '"]'
      );
      var li = card && card.closest('li');
      if (li) li.remove();
      if (!host.querySelector('[data-boreal-card]')) showEmpty();
    });

    render();
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ------------------------------------------------------- quick add (touch) */

  function closeAllSizePanels(except) {
    document.querySelectorAll('.bcard.is-sizes-open').forEach(function (c) {
      if (c !== except) c.classList.remove('is-sizes-open');
    });
  }

  /* A direct add has no size panel to report into, so the button itself is the
     only place feedback can go. */
  function flashAdded(btn) {
    window.clearTimeout(btn._addedTimer);
    btn.classList.add('is-added');
    btn._addedTimer = window.setTimeout(function () {
      btn.classList.remove('is-added');
    }, 2200);
  }

  function initQuickAdd(scope) {
    scope.querySelectorAll('[data-bcard-quick]').forEach(function (btn) {
      if (btn.dataset.quickBound) return;
      btn.dataset.quickBound = 'true';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var card = btn.closest('.bcard');
        if (!card) return;

        /* No size option on this product — there is nothing to ask, so add the
           selected colourway straight to the bag. */
        if (btn.hasAttribute('data-bcard-direct')) {
          var inst = card.__bcard;
          if (!inst) return;
          var available = inst.variantsForColour().filter(function (v) {
            return v.a;
          });
          if (!available.length) return;
          flashAdded(btn);
          inst.addToCart(available[0].id, btn);
          return;
        }

        var open = card.classList.contains('is-sizes-open');
        closeAllSizePanels(card);
        card.classList.toggle('is-sizes-open', !open);
      });
    });

    scope.querySelectorAll('[data-bcard-sizes-close]').forEach(function (btn) {
      if (btn.dataset.quickBound) return;
      btn.dataset.quickBound = 'true';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var card = btn.closest('.bcard');
        if (card) card.classList.remove('is-sizes-open');
      });
    });

    if (!document.body.dataset.bcQuickGlobal) {
      document.body.dataset.bcQuickGlobal = 'true';
      document.addEventListener('click', function (e) {
        if (!e.target.closest('.bcard.is-sizes-open')) closeAllSizePanels(null);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllSizePanels(null);
      });
    }
  }

  /* -------------------------------------------------------------- density */

  var DENSITY_KEY = 'boreal:density';

  function initDensity(scope) {
    var grid = scope.querySelector('[data-bc-grid]');
    var buttons = scope.querySelectorAll('[data-bc-density]');
    if (!grid || !buttons.length) return;

    var stored = null;
    try {
      stored = window.localStorage.getItem(DENSITY_KEY);
    } catch (e) {
      /* ignore */
    }

    function apply(value) {
      grid.setAttribute('data-density', value);
      buttons.forEach(function (b) {
        b.classList.toggle('is-active', b.getAttribute('data-bc-density') === value);
      });
      try {
        window.localStorage.setItem(DENSITY_KEY, value);
      } catch (e) {
        /* ignore */
      }
    }

    var allowed = Array.prototype.map.call(buttons, function (b) {
      return b.getAttribute('data-bc-density');
    });
    apply(stored && allowed.indexOf(stored) > -1 ? stored : grid.getAttribute('data-density'));

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        apply(btn.getAttribute('data-bc-density'));
      });
    });
  }

  /* --------------------------------------------------------------- facets */

  function Facets(section) {
    this.section = section;
    this.id = section.getAttribute('data-section-id');
    this.bind();
  }

  Facets.prototype.bind = function () {
    var self = this;

    /* Desktop dropdown panels */
    this.section.querySelectorAll('[data-bc-filter]').forEach(function (filter) {
      var toggle = filter.querySelector('[data-bc-filter-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = filter.classList.contains('is-open');
        self.section.querySelectorAll('[data-bc-filter]').forEach(function (f) {
          f.classList.remove('is-open');
        });
        filter.classList.toggle('is-open', !open);
        toggle.setAttribute('aria-expanded', String(!open));
      });
    });

    /* navigate() re-runs `new Facets(wrapper)` on the SAME element after every
       filter or sort change. Without teardown each pass left another pair of
       document-level listeners behind, and each one re-queries the section on
       every click and keypress in the page — the collection gets measurably
       heavier the longer someone browses it. */
    this.onDoc('click', function (e) {
      if (!self.section.contains(e.target)) {
        self.section.querySelectorAll('[data-bc-filter]').forEach(function (f) {
          f.classList.remove('is-open');
          var t = f.querySelector('[data-bc-filter-toggle]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
    });

    this.onDoc('keydown', function (e) {
      if (e.key !== 'Escape') return;
      self.section.querySelectorAll('[data-bc-filter]').forEach(function (f) {
        f.classList.remove('is-open');
        var t = f.querySelector('[data-bc-filter-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
      self.closeModal();
    });

    /* Modals */
    this.section.querySelectorAll('[data-bc-modal-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.openModal(btn.getAttribute('data-bc-modal-open'));
      });
    });
    this.section.querySelectorAll('[data-bc-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal();
      });
    });

    /* :has() fallback so ticked chips still highlight on older engines */
    this.section.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('input[type="checkbox"], input[type="radio"]')) return;
      var group = input.closest('[data-bc-group], .bc-sortlist');
      if (!group) return;
      group.querySelectorAll('input').forEach(function (i) {
        var label = i.closest('label');
        if (label) label.classList.toggle('is-checked', i.checked);
      });
    });

    /* Price is a pair of number inputs, which match none of the selectors the
       handler below tests for — so on desktop a typed price range was collected
       by buildUrl() but nothing ever called it. Blur commits it; Enter commits
       it immediately. */
    this.section.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('input[type="number"]')) return;
      if (input.closest('[data-bc-modal]')) return; /* modal waits for Apply */
      self.submit();
    });

    this.section.addEventListener('keydown', function (e) {
      var input = e.target;
      if (e.key !== 'Enter') return;
      if (!input.matches || !input.matches('input[type="number"]')) return;
      e.preventDefault();
      if (input.closest('[data-bc-modal]')) return;
      self.submit();
    });

    /* Any checkbox or radio change re-runs the query */
    this.section.addEventListener('change', function (e) {
      var input = e.target;
      if (!input.matches || !input.matches('input[type="checkbox"], input[type="radio"], select[name="sort_by"]')) return;
      if (input.closest('[data-bc-modal]')) {
        self.syncModalCount();
        return; /* modal waits for Apply */
      }
      self.submit();
    });

    /* Apply / reset */
    this.section.querySelectorAll('[data-bc-apply]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal();
        self.submit(btn.closest('[data-bc-modal]'));
      });
    });

    this.section.querySelectorAll('[data-bc-clear]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.clearAll();
      });
    });

    /* Active filter chips */
    this.section.querySelectorAll('[data-bc-remove]').forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        self.navigate(chip.getAttribute('href'));
      });
    });
  };

  function focusableIn(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('button, [href], input, select, textarea, [tabindex]'),
      function (n) {
        return (
          !n.disabled &&
          n.tabIndex !== -1 &&
          n.offsetWidth > 0 &&
          n.offsetHeight > 0
        );
      }
    );
  }

  Facets.prototype.onDoc = function (type, fn) {
    this._doc = this._doc || [];
    this._doc.push([type, fn]);
    document.addEventListener(type, fn);
  };

  Facets.prototype.teardown = function () {
    (this._doc || []).forEach(function (pair) {
      document.removeEventListener(pair[0], pair[1]);
    });
    this._doc = [];
  };

  Facets.prototype.openModal = function (name) {
    var modal = this.section.querySelector('[data-bc-modal="' + name + '"]');
    if (!modal) return;
    modal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.openModalEl = modal;

    /* The markup declares aria-modal="true", which tells assistive tech to hide
       everything outside the dialog. Without moving focus in, a screen reader
       user is left on the trigger button the browser has just hidden. */
    this.lastFocus = document.activeElement;
    /* Query for real focusable controls. The first [data-bc-modal-close] in the
       markup is the overlay <div>, and calling .focus() on a div with no
       tabindex silently does nothing — focus stayed on the trigger behind the
       dialog. */
    var focusables = focusableIn(modal);
    if (focusables.length) {
      window.setTimeout(function () {
        focusables[0].focus();
      }, 60);
    }

    var self = this;
    if (!modal.dataset.trapBound) {
      modal.dataset.trapBound = 'true';
      modal.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var items = focusableIn(modal);
        if (!items.length) return;
        var firstEl = items[0];
        var lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      });
    }
    void self;
  };

  Facets.prototype.closeModal = function () {
    this.section.querySelectorAll('[data-bc-modal]').forEach(function (m) {
      m.classList.remove('is-open');
    });
    document.body.classList.remove('overflow-hidden');
    this.openModalEl = null;
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
      this.lastFocus = null;
    }
  };

  Facets.prototype.syncModalCount = function () {
    /* Live count would need an extra request per keystroke; instead we show how
       many boxes are ticked, which is what the shopper is actually tracking. */
    var modal = this.openModalEl;
    if (!modal) return;
    var n = modal.querySelectorAll('input[type="checkbox"]:checked').length;
    var target = modal.querySelector('[data-bc-modal-count]');
    if (target) target.textContent = n ? '(' + n + ')' : '';
  };

  Facets.prototype.buildUrl = function (scope) {
    var base = this.section.getAttribute('data-collection-url') || window.location.pathname;
    var params = new URLSearchParams(window.location.search);
    params.delete('page');

    /* The submitting UI owns every parameter it renders an input for. Wipe
       those, then rewrite them from what is currently ticked. Everything else
       in the URL (the other UI's filters, the sort order) is left alone. */
    var container =
      scope || this.section.querySelector('[data-bc-facet-scope="desktop"]') || this.section;

    var owned = {};
    container.querySelectorAll('input[name], select[name]').forEach(function (input) {
      owned[input.name] = true;
    });
    Object.keys(owned).forEach(function (name) {
      params.delete(name);
    });

    container.querySelectorAll('input[type="checkbox"]:checked').forEach(function (input) {
      params.append(input.name, input.value);
    });
    container
      .querySelectorAll('input[type="number"], input[type="text"]')
      .forEach(function (input) {
        if (input.name && input.value) params.append(input.name, input.value);
      });

    var sortEl =
      container.querySelector('select[name="sort_by"]') ||
      container.querySelector('input[name="sort_by"]:checked');
    if (sortEl && sortEl.value) params.set('sort_by', sortEl.value);

    var qs = params.toString();
    return qs ? base + '?' + qs : base;
  };

  Facets.prototype.clearAll = function () {
    this.navigate(this.section.getAttribute('data-collection-url') || window.location.pathname);
  };

  Facets.prototype.submit = function (scope) {
    this.navigate(this.buildUrl(scope));
  };

  Facets.prototype.navigate = function (url) {
    var self = this;
    var wrapper = this.section;
    wrapper.classList.add('is-loading');

    var fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + this.id;

    fetch(fetchUrl)
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-boreal-collection]');
        if (!fresh) {
          window.location.href = url;
          return;
        }
        wrapper.innerHTML = fresh.innerHTML;
        window.history.pushState({ boreal: true }, '', url);
        lastSearch = window.location.search;
        wrapper.classList.remove('is-loading');
        init(wrapper);
        mountFacets(wrapper);
        var grid = wrapper.querySelector('[data-bc-grid]');
        if (grid) {
          var top = wrapper.getBoundingClientRect().top + window.scrollY - 120;
          if (window.scrollY > top) window.scrollTo({ top: top, behavior: 'smooth' });
        }
      })
      .catch(function () {
        window.location.href = url;
      });
  };

  /* --------------------------------------------------------- back to top */

  var scrollHandler = null;

  function initBackToTop(scope) {
    var btn = scope.querySelector('[data-bc-top]');
    var grid = scope.querySelector('[data-bc-grid]');
    if (!btn || !grid) return;

    if (!btn.dataset.bound) {
      btn.dataset.bound = 'true';
      btn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
    scrollHandler = function () {
      btn.classList.toggle('is-visible', grid.getBoundingClientRect().top < -200);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
    scrollHandler();
  }

  /* ------------------------------------------------------------------ init */

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-boreal-card]').forEach(function (card) {
      if (card.getAttribute('data-boreal-card-ready')) return;
      new Card(card);
    });
    initWishlist(root);
    initWishlistPage(root);
    initQuickAdd(root);
    initDensity(root);
    initBackToTop(root);
  }

  /* One instance per section element, tracked on the node. The theme editor
     re-fires shopify:section:load on every setting change, so without this a
     merchant tweaking the grid stacks a new Facets (and its document
     listeners) on each keystroke. */
  function mountFacets(section) {
    if (section._bcFacets && section._bcFacets.teardown) section._bcFacets.teardown();
    section._bcFacets = new Facets(section);
  }

  function boot() {
    init(document);
    document.querySelectorAll('[data-boreal-collection]').forEach(mountFacets);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Reload only when the collection query actually changed. The old handler
     fired on any history pop — including plain #hash links — forcing a full
     round trip to leave a page the shopper had merely scrolled. */
  window.addEventListener('popstate', function () {
    if (window.location.search === lastSearch) return;
    lastSearch = window.location.search;
    window.location.reload();
  });

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-boreal-collection]');
    if (section) {
      init(section);
      mountFacets(section);
    }
  });

  window.BorealCollectionInit = init;
})();
